import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Data Table Controller
// Rails 8 / Hotwire replacement for jquery-tablesorter
// Works with table_sortable gem's server-side API
//
// Usage:
//   div[data-controller="data-table"
//       data-data-table-url-value="/admin/years/1/program_operators.json"
//       data-data-table-page-size-value="30"]
//
export default class extends Controller {
  static targets = ["thead", "tbody", "pagination", "info", "loading", "selectionInfo", "selectionCount", "massActions", "columnToggle", "resetFiltersButton", "columnSelectorCheckbox", "aggregateRow", "aggregateToggle", "aggregateDivider", "aggregateCheckbox"]

  static values = {
    url: String,           // JSON endpoint (required)
    pageSize: Number,      // Records per page
    page: { type: Number, default: 0 },  // Current page (0-indexed!)
    exportAction: String,  // Export action name (optional, defaults to 'export_index')
    showAggregates: { type: Boolean, default: false },  // Whether to show aggregate row
    aggregateToggleUrl: String  // URL to toggle aggregate visibility
  }

  connect() {
    logger.log("DataTable controller connected")
    logger.log("URL:", this.urlValue)
    logger.log("Page size:", this.pageSizeValue)

    // Get translations from window.I18n (set by javascript_translations helper)
    this.i18n = window.I18n?.data_table || {}

    // Initialize state
    this.filters = {}      // Column filters: {columnIndex: value}
    this.sorts = []        // Column sorts: [{column: index, dir: 'asc'|'desc'}]
    this.selected = new Set()  // Selected record IDs
    this.allIds = []       // All IDs matching current filter (for select all)
    this.visibleColumns = new Set()  // Column indices that are visible (Phase 4 - optional)
    this.showAll = false   // Flag to track if user wants to show all records
    this.aggregates = {}   // Aggregate values: {columnName: value}

    // Check if page size is -1 (meaning "show all")
    // We'll update it to actual total after loading data
    if (this.pageSizeValue === -1) {
      this.showAll = true
      logger.log("Show all mode enabled (-1)")
    }

    // Initialize filters from existing input values (e.g., filter_initial_value from server)
    this.initializeFiltersFromInputs()

    // Initialize column visibility (Phase 4 - optional)
    this.initializeColumnVisibility()

    // Initialize sorts from URL parameters (server sets default sort via params)
    this.initializeSortsFromUrl()

    // Strip scol params from base URL to prevent duplication when user sorts
    // The sorts are now stored in this.sorts, so we don't need them in the URL
    this.stripScolFromBaseUrl()

    // Load initial data
    this.loadData()
  }

  // Fetch data from table_sortable JSON API
  async loadData(options = {}) {
    const url = this.buildUrl(options)
    logger.log("Loading data from:", url)

    this.showLoading()

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      logger.log("Received data:", data)

      this.updateTable(data)
    } catch (error) {
      logger.error("Error loading data:", error)
      this.showError(error.message)
    } finally {
      this.hideLoading()
    }
  }

  // Build URL with query parameters for table_sortable
  buildUrl(options = {}) {
    let url = this.urlValue
    const params = new URLSearchParams()

    // Pagination
    // If showAll is true or pageSize is -1, use a very large number for the request
    const pageSize = (this.showAll || this.pageSizeValue === -1) ? 999999 : this.pageSizeValue
    params.append('page', this.pageValue)
    params.append('pagesize', pageSize)

    // Filters: fcol[1]=value&fcol[2]=value...
    // Compound filters (date ranges, etc): fcol[1]_from=value&fcol[1]_to=value
    Object.entries(this.filters).forEach(([columnIndex, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        if (typeof value === 'object') {
          // Compound filter (date range, text+checkbox, etc.)
          // Send each non-empty key as fcol[N]_key=value
          Object.entries(value).forEach(([key, subValue]) => {
            if (subValue !== '' && subValue !== null && subValue !== undefined) {
              params.append(`fcol[${columnIndex}]_${key}`, subValue)
            }
          })
        } else {
          // Regular filter (string value)
          params.append(`fcol[${columnIndex}]`, value)
        }
      }
    })

    // Sorts: scol[1]=0 (0=asc, 1=desc)
    // table_sortable expects column index as key, 0/1 as value
    this.sorts.forEach((sort) => {
      params.append(`scol[${sort.column}]`, sort.dir === 'asc' ? '0' : '1')
    })

    // Optional: reload headers (for column visibility changes)
    if (options.reloadHeaders) {
      params.append('reload_headers', 'true')
    }

    // Include aggregates if enabled (either from Stimulus value or explicit option)
    if (this.showAggregatesValue || options.includeAggregates) {
      params.append('include_aggregates', 'true')
    }

    // Append params to URL
    const separator = url.includes('?') ? '&' : '?'
    const finalUrl = `${url}${separator}${params.toString()}`

    logger.log("Built URL:", finalUrl)

    return finalUrl
  }

  // Update table with new data
  updateTable(data) {
    logger.log("Update table with data:", data)

    // If showAll flag is set and we have a total, update page size to match total
    if (this.showAll && data.total !== undefined && this.pageSizeValue !== data.total) {
      this.pageSizeValue = data.total
      logger.log("Updated page size to total for show all:", data.total)
    }

    // Store all IDs for select all functionality
    if (data.all_ids) {
      this.allIds = data.all_ids
      logger.log("All IDs:", this.allIds.length, "total")
    }

    // Update thead with HTML from server (for column visibility changes)
    if (this.hasTheadTarget && data.headers) {
      // Preserve the aggregate row before replacing thead content
      const aggregateRow = this.hasAggregateRowTarget ? this.aggregateRowTarget : null
      const aggregateRowParent = aggregateRow?.parentNode

      this.theadTarget.innerHTML = data.headers

      // Re-append the aggregate row if it existed
      if (aggregateRow && aggregateRowParent === this.theadTarget) {
        this.theadTarget.appendChild(aggregateRow)
      }

      // Restore filter values and sort icons after rebuilding headers
      this.restoreFilterValues()
      this.updateSortIcons()
      this.updateResetFiltersButtonVisibility()

      logger.log("Updated thead with new headers")
    } else if (this.hasTheadTarget) {
      // On initial load (no header reload), still update sort icons
      // This ensures default sort icons are displayed
      this.updateSortIcons()
    }

    // Update tbody with HTML rows from server
    if (this.hasTbodyTarget) {
      if (data.rows && data.rows.trim() !== '') {
        this.tbodyTarget.innerHTML = data.rows

        // Add Stimulus-compatible checkboxes (Phase 3)
        this.addSelectionCheckboxes()

        logger.log("Updated tbody with new rows")
      } else {
        // Show empty state
        const colCount = this.element.querySelectorAll('thead th').length
        const noResultsText = this.i18n.no_results || 'No results'
        this.tbodyTarget.innerHTML = `
          <tr>
            <td colspan="${colCount}" class="text-center text-muted p-4">
              ${noResultsText}
            </td>
          </tr>
        `
        logger.log("No results - showing empty state")
      }
    }

    // Update pagination (both top and bottom)
    if (data.total !== undefined) {
      this.lastTotal = data.total  // Store for selection updates
      this.updatePagination(data.total)
    }

    // Update info display
    if (this.hasInfoTarget) {
      this.updateInfo(data)
    }

    // Handle aggregates toggle button visibility
    if (data.has_aggregatable_columns !== undefined) {
      this.updateAggregateToggleVisibility(data.has_aggregatable_columns)
    }

    // Handle aggregates row
    // Check if aggregates object has any actual values (empty {} is truthy but has no data)
    const hasAggregateData = data.aggregates && Object.keys(data.aggregates).length > 0
    logger.log('Aggregates check:', {
      show_aggregates: data.show_aggregates,
      has_aggregates: !!data.aggregates,
      has_aggregate_data: hasAggregateData,
      has_aggregatable_columns: data.has_aggregatable_columns,
      showAggregatesValue: this.showAggregatesValue
    })
    if (data.show_aggregates && hasAggregateData) {
      this.aggregates = data.aggregates
      this.showAggregatesValue = true
      this.renderAggregateRow()
    } else if (data.show_aggregates && !hasAggregateData && data.has_aggregatable_columns) {
      // User preference is on, but aggregates weren't included in response
      // (e.g., column became visible again). Re-fetch with aggregates.
      logger.log('Re-fetching with aggregates...')
      this.showAggregatesValue = true
      // Hide row while re-fetching
      if (this.hasAggregateRowTarget) {
        this.aggregateRowTarget.classList.add('d-none')
      }
      this.loadData({ includeAggregates: true })
      return // Don't continue processing, will be handled by the re-fetch
    } else {
      // Hide aggregate row in all other cases
      this.showAggregatesValue = data.show_aggregates || false
      if (this.hasAggregateRowTarget) {
        this.aggregateRowTarget.classList.add('d-none')
        this.aggregateRowTarget.innerHTML = '' // Clear content to prevent height
      }
    }

    // Emit custom event for other controllers to listen to
    this.element.dispatchEvent(new CustomEvent('data-table:loaded', {
      detail: { data },
      bubbles: true
    }))
  }

  // Update pagination controls (styled like original table_sortable)
  updatePagination(total) {
    const totalPages = Math.ceil(total / this.pageSizeValue) || 1
    const currentPage = this.pageValue  // 0-indexed
    const displayPage = currentPage + 1  // 1-indexed for display

    // Build pagination HTML matching original table_sortable styling
    const html = `
      <ul class="pagination justify-content-center mb-0 p-0">
        <li class="page-item ${currentPage === 0 ? 'disabled' : ''}">
          <a href="#" class="page-link first" data-action="click->data-table#goToPage" data-page="0" title="First">
            <i class="fa fa-fast-backward"></i>
          </a>
        </li>
        <li class="page-item ${currentPage === 0 ? 'disabled' : ''}">
          <a href="#" class="page-link prev" data-action="click->data-table#goToPage" data-page="${currentPage - 1}" title="Previous">
            <i class="fa fa-step-backward"></i>
          </a>
        </li>
        <li class="page-item disabled">
          <span class="page-link pagedisplay">
            ${this.i18n.page_display ?
              this.i18n.page_display.replace('{current}', displayPage).replace('{total}', totalPages) :
              `${displayPage} / ${totalPages}`}
          </span>
        </li>
        <li class="page-item ${currentPage === totalPages - 1 ? 'disabled' : ''}">
          <a href="#" class="page-link next" data-action="click->data-table#goToPage" data-page="${currentPage + 1}" title="Next">
            <i class="fa fa-step-forward"></i>
          </a>
        </li>
        <li class="page-item ${currentPage === totalPages - 1 ? 'disabled' : ''}">
          <a href="#" class="page-link last" data-action="click->data-table#goToPage" data-page="${totalPages - 1}" title="Last">
            <i class="fa fa-fast-forward"></i>
          </a>
        </li>
      </ul>`

    // Update pagination target
    if (this.hasPaginationTarget) {
      try {
        this.paginationTarget.innerHTML = html
      } catch (e) {
        logger.error('Error updating pagination:', e)
      }
    }
  }

  // Update info display (record counts, etc.)
  updateInfo(data) {
    if (data.total !== undefined) {
      if (data.total === 0) {
        this.infoTarget.textContent = this.i18n.no_records || 'No records to display'
      } else {
        const start = this.pageValue * this.pageSizeValue + 1
        const end = Math.min((this.pageValue + 1) * this.pageSizeValue, data.total)

        // Render info display with per-page selector
        this.renderInfoWithPerPageSelector(start, end, data.total)
      }
    }
  }

  // Render info display with embedded per-page selector dropdown
  renderInfoWithPerPageSelector(start, end, total) {
    const controllerName = this.element.dataset.controllerName || 'unknown'

    // Get translations
    const showing = this.i18n.info_display_showing || 'מציג'
    const of = this.i18n.info_display_of || 'מתוך'
    const records = this.i18n.info_display_records || 'רשומות'
    const allLabel = this.i18n.per_page?.all || 'הכל'

    // Determine current selected value (match current page size or 'all')
    const currentValue = (this.pageSizeValue === total) ? 'all' : this.pageSizeValue.toString()

    // Build options HTML - include common sizes plus current value if not in list
    let sizes = [10, 25, 50, 100]

    // If current page size is not in the standard list and not 'all', add it
    if (!sizes.includes(this.pageSizeValue) && this.pageSizeValue !== total) {
      sizes.push(this.pageSizeValue)
      sizes.sort((a, b) => a - b)  // Keep sorted
    }

    const options = sizes.map(size => {
      const selected = (this.pageSizeValue === size) ? 'selected' : ''
      return `<option value="${size}" ${selected}>${size}</option>`
    }).join('')

    // Add "All" option
    const allSelected = (this.pageSizeValue === total) ? 'selected' : ''
    const allOption = `<option value="all" ${allSelected}>${allLabel}</option>`

    // Build complete HTML with inline select dropdown
    const html = `
      <span>${showing} ${start}-</span>
      <select class="form-select form-select-sm d-inline-block mx-1"
              style="width: auto; min-width: 70px;"
              data-controller="data-table-per-page"
              data-data-table-per-page-controller-name-value="${controllerName}"
              data-data-table-per-page-total-value="${total}"
              data-action="change->data-table-per-page#change">
        ${options}
        ${allOption}
      </select>
      <span>${of} ${total} ${records}</span>
    `

    this.infoTarget.innerHTML = html
  }

  // Handle pagination click
  goToPage(event) {
    event.preventDefault()

    const link = event.currentTarget
    if (link.parentElement.classList.contains('disabled')) {
      return
    }

    const page = parseInt(link.dataset.page)
    if (page >= 0) {  // 0-indexed
      this.pageValue = page
      this.loadData()

      // Scroll to top of table
      this.element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Handle column header click for sorting
  sort(event) {
    const header = event.currentTarget
    const columnIndex = parseInt(header.dataset.column)

    logger.log("Sort clicked - column index:", columnIndex)

    if (isNaN(columnIndex)) {
      logger.warn("Invalid column index for sorting")
      return
    }

    // Find existing sort for this column
    const existingSort = this.sorts.find(s => s.column === columnIndex)

    if (existingSort) {
      // Toggle direction
      existingSort.dir = existingSort.dir === 'asc' ? 'desc' : 'asc'
    } else {
      // Add new sort (single column for now, can support multi-column later)
      this.sorts = [{ column: columnIndex, dir: 'asc' }]
    }

    logger.log("Sorts array:", this.sorts)

    // Update UI - add/remove sort icons
    this.updateSortIcons()

    // Reset to page 0 when sorting changes
    this.pageValue = 0

    // Reload data
    this.loadData()
  }

  // Update sort icons in headers
  updateSortIcons() {
    logger.log('🎨 updateSortIcons called, sorts:', this.sorts)

    // Remove all existing sort icons
    const existingIcons = this.element.querySelectorAll('th .sort-icon')
    logger.log('📍 Found', existingIcons.length, 'existing sort-icon spans')
    existingIcons.forEach(icon => {
      icon.innerHTML = ''
    })

    // Add icons for active sorts
    this.sorts.forEach(sort => {
      logger.log(`🔎 Looking for header with data-column="${sort.column}"`)
      const header = this.element.querySelector(`th[data-column="${sort.column}"]`)
      if (header) {
        logger.log('✓ Found header:', header.textContent.trim())
        const icon = header.querySelector('.sort-icon')
        if (icon) {
          const iconHtml = sort.dir === 'asc'
            ? '<i class="fa fa-caret-up"></i>'
            : '<i class="fa fa-caret-down"></i>'
          icon.innerHTML = iconHtml
          logger.log(`✅ Added ${sort.dir} icon to column ${sort.column}`)
        } else {
          logger.warn(`⚠️ Header found but no .sort-icon span inside`)
        }
      } else {
        logger.warn(`⚠️ No header found with data-column="${sort.column}"`)
        // Log all headers for debugging
        const allHeaders = this.element.querySelectorAll('th[data-column]')
        logger.log('Available headers:', Array.from(allHeaders).map(h => ({
          column: h.dataset.column,
          text: h.textContent.trim()
        })))
      }
    })
  }

  // Handle filter input changes
  filter(event) {
    const input = event.target
    const columnIndex = parseInt(input.dataset.column)
    const value = input.value

    if (isNaN(columnIndex)) {
      logger.warn("Invalid column index for filtering")
      return
    }

    // Update filter state
    this.filters[columnIndex] = value

    // Update reset button visibility
    this.updateResetFiltersButtonVisibility()

    // Reset to page 0 when filters change
    this.pageValue = 0

    // Debounce text inputs, immediate for selects
    if (input.tagName === 'SELECT') {
      this.loadData()
    } else {
      this.debounceLoadData()
    }
  }

  // Handle date range filter changes (from/to)
  filterDateRange(event) {
    const input = event.target
    const columnIndex = parseInt(input.dataset.column)
    const datePart = input.dataset.datePart  // 'from' or 'to'
    const value = input.value

    if (isNaN(columnIndex)) {
      logger.warn("Invalid column index for date range filtering")
      return
    }

    // Initialize date range object if needed
    if (!this.filters[columnIndex] || typeof this.filters[columnIndex] !== 'object') {
      this.filters[columnIndex] = { from: '', to: '' }
    }

    // Update the specific part (from or to)
    this.filters[columnIndex][datePart] = value

    logger.log(`Date range filter updated - column ${columnIndex}, ${datePart}:`, value)

    // Update reset button visibility
    this.updateResetFiltersButtonVisibility()

    // Reset to page 0 when filters change
    this.pageValue = 0

    // Debounce the data loading
    this.debounceLoadData()
  }

  // Handle compound filter changes (e.g., text + checkbox for assignments)
  filterCompound(event) {
    const input = event.target
    const columnIndex = parseInt(input.dataset.column)
    const filterPart = input.dataset.filterPart  // e.g., 'text', 'include_inactive'

    // Get value - handle checkboxes vs text inputs
    const value = input.type === 'checkbox' ? input.checked.toString() : input.value

    if (isNaN(columnIndex)) {
      logger.warn("Invalid column index for compound filtering")
      return
    }

    // Initialize compound filter object if needed
    if (!this.filters[columnIndex] || typeof this.filters[columnIndex] !== 'object') {
      this.filters[columnIndex] = {}
    }

    // Update the specific part
    this.filters[columnIndex][filterPart] = value

    logger.log(`Compound filter updated - column ${columnIndex}, ${filterPart}:`, value)

    // Update reset button visibility
    this.updateResetFiltersButtonVisibility()

    // Reset to page 0 when filters change
    this.pageValue = 0

    // Debounce text inputs, immediate for checkboxes
    if (input.type === 'checkbox') {
      this.loadData()
    } else {
      this.debounceLoadData()
    }
  }

  // Debounced data loading for text inputs
  debounceLoadData() {
    clearTimeout(this.loadDataTimeout)
    this.loadDataTimeout = setTimeout(() => {
      this.loadData()
    }, 300) // 300ms delay
  }

  // Show loading indicator
  showLoading() {
    if (this.hasLoadingTarget) {
      // Modern layout: loading overlay covers the entire scroll wrapper
      // Position is handled by CSS (position: absolute within .data-table-scroll-wrapper)
      this.loadingTarget.classList.remove('d-none')
    }
    this.element.classList.add('loading')
  }

  // Hide loading indicator
  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
    this.element.classList.remove('loading')
  }

  // Show error message
  showError(message) {
    // TODO: Add better error UI
    logger.error("Data table error:", message)
    if (this.hasTbodyTarget) {
      const errorText = (this.i18n.error || 'Error loading data: {message}')
        .replace('{message}', message)
      this.tbodyTarget.innerHTML = `
        <tr>
          <td colspan="99" class="text-center text-danger p-4">
            <i class="fa fa-exclamation-triangle"></i>
            ${errorText}
          </td>
        </tr>
      `
    }
  }

  // Public API: Refresh the table
  refresh() {
    this.loadData()
  }

  // Public API: Reset all filters (clears state AND triggers a refresh
  // fetch). Use this when the user explicitly clicks "Reset filters".
  resetFilters() {
    this.clearFiltersInPlace()
    this.loadData()
  }

  // Public API: Clear filter state and inputs WITHOUT triggering a fetch.
  // Caller is responsible for invoking loadData() afterwards. Used by the
  // column-selector panel reload path — it needs to clear filters and
  // fire exactly ONE loadData with reloadHeaders + includeAggregates, so
  // it can't go through resetFilters (which would race a second fetch).
  clearFiltersInPlace() {
    this.filters = {}
    this.pageValue = 0

    // Clear filter inputs
    this.element.querySelectorAll('input[data-column], select[data-column]').forEach(input => {
      input.value = ''
    })

    // Clear date range inputs (they have data-date-part)
    this.element.querySelectorAll('input[data-date-part]').forEach(input => {
      input.value = ''
      const datepickerController = this.application.getControllerForElementAndIdentifier(
        input.closest('[data-controller~="datepicker"]'),
        'datepicker'
      )
      if (datepickerController) {
        datepickerController.clear()
      }
    })

    // Clear compound filter inputs (they have data-filter-part)
    this.element.querySelectorAll('input[data-filter-part]').forEach(input => {
      if (input.type === 'checkbox') {
        input.checked = false
      } else {
        input.value = ''
      }
    })

    this.updateResetFiltersButtonVisibility()
  }

  // Initialize filters from current input values (captures filter_initial_value from server)
  initializeFiltersFromInputs() {
    this.element.querySelectorAll('input[data-column], select[data-column]').forEach(input => {
      const columnIndex = parseInt(input.dataset.column)
      if (isNaN(columnIndex)) return

      // Skip compound filter inputs (they have data-filter-part attribute)
      // These are handled separately by filterCompound
      if (input.dataset.filterPart || input.dataset.datePart) return

      // If input has a value, use it
      if (input.value !== '') {
        this.filters[columnIndex] = input.value
        logger.log('Initialized filter from input:', columnIndex, '=', input.value)
      }
      // Otherwise, check for default value (for selects with filter_initial_value)
      else if (input.dataset.defaultValue) {
        this.filters[columnIndex] = input.dataset.defaultValue
        input.value = input.dataset.defaultValue
        logger.log('Applied default filter:', columnIndex, '=', input.dataset.defaultValue)
      }
    })

    // Update reset button visibility after initialization
    this.updateResetFiltersButtonVisibility()
  }

  // Initialize sorts from URL parameters
  // The server sets default sort via params[:scol], which gets included in the JSON URL
  initializeSortsFromUrl() {
    // Parse the URL to extract scol parameters
    const url = new URL(this.urlValue, window.location.origin)
    const params = new URLSearchParams(url.search)

    logger.log('🔍 Initializing sorts from URL:', this.urlValue)

    // table_sortable format: scol[column_index]=0 (asc) or scol[column_index]=1 (desc)
    // Look for any scol parameters in the URL
    for (const [key, value] of params.entries()) {
      const match = key.match(/^scol\[(\d+)\]$/)
      if (match) {
        const columnIndex = parseInt(match[1])
        const dir = (value === '1') ? 'desc' : 'asc'

        this.sorts.push({ column: columnIndex, dir: dir })
        logger.log('✓ Initialized sort from URL:', columnIndex, dir)
      }
    }

    logger.log('📊 Total sorts found:', this.sorts.length)

    // Update sort icons to reflect initial state
    if (this.sorts.length > 0) {
      logger.log('🎨 Calling updateSortIcons() from initializeSortsFromUrl')
      this.updateSortIcons()
    }
  }

  // Strip scol parameters from base URL
  // After initializing this.sorts from URL params, we remove scol from the base URL
  // This prevents duplication when buildUrl() appends new sort params
  stripScolFromBaseUrl() {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      const params = new URLSearchParams(url.search)

      logger.log('🧹 Stripping scol params from base URL:', this.urlValue)

      // Find all scol parameters to remove
      const keysToDelete = []
      for (const key of params.keys()) {
        if (key.match(/^scol\[/)) {
          keysToDelete.push(key)
          logger.log('  Removing:', key, '=', params.get(key))
        }
      }

      // Remove scol parameters
      keysToDelete.forEach(key => params.delete(key))

      // Rebuild URL without scol params
      url.search = params.toString()
      this.urlValue = url.pathname + (url.search ? '?' + url.search : '')

      logger.log('✓ Base URL after stripping scol:', this.urlValue)
    } catch (error) {
      logger.error('Error stripping scol from URL:', error)
      // If parsing fails, keep original URL (graceful degradation)
    }
  }

  // Restore filter values to inputs (after rebuilding headers)
  restoreFilterValues() {
    Object.entries(this.filters).forEach(([columnIndex, value]) => {
      // Check if this is a date range filter
      if (typeof value === 'object' && 'from' in value && 'to' in value) {
        // Restore date range inputs
        const fromInput = this.element.querySelector(`input[data-column="${columnIndex}"][data-date-part="from"]`)
        const toInput = this.element.querySelector(`input[data-column="${columnIndex}"][data-date-part="to"]`)

        if (fromInput) {
          fromInput.value = value.from || ''
        }
        if (toInput) {
          toInput.value = value.to || ''
        }
      } else {
        // Regular filter input
        const input = this.element.querySelector(`input[data-column="${columnIndex}"], select[data-column="${columnIndex}"]`)
        if (input) {
          // Restore the value even if it's empty (to clear filters)
          input.value = value !== null && value !== undefined ? value : ''
        }
      }
    })
  }

  // Check if any filters are currently active
  hasActiveFilters() {
    return Object.values(this.filters).some(value => {
      if (value === '' || value === null || value === undefined) {
        return false
      }
      // Check date range filters (must have at least one part filled)
      if (typeof value === 'object' && 'from' in value && 'to' in value) {
        return (value.from && value.from !== '') || (value.to && value.to !== '')
      }
      return true
    })
  }

  // Update reset filters button visibility based on active filters
  updateResetFiltersButtonVisibility() {
    if (!this.hasResetFiltersButtonTarget) {
      return
    }

    if (this.hasActiveFilters()) {
      this.resetFiltersButtonTarget.classList.remove('d-none')
    } else {
      this.resetFiltersButtonTarget.classList.add('d-none')
    }
  }

  // Cleanup
  disconnect() {
    clearTimeout(this.loadDataTimeout)
  }

  // ========================================
  // Navigation Methods
  // ========================================

  // Navigate to a URL when clicking a row (Turbo-friendly)
  navigateToRow(event) {
    // Ignore clicks on links, buttons, or inputs within the row
    if (event.target.closest('a, button, input, select, textarea')) {
      return
    }

    const url = event.currentTarget.dataset.url
    if (url) {
      // Clear Turbo cache to prevent stale page state after navigation
      Turbo.cache.clear()
      Turbo.visit(url)
    }
  }

  // ========================================
  // Selection Methods (Phase 3)
  // ========================================

  // Add Stimulus-compatible checkboxes to loaded rows
  addSelectionCheckboxes() {
    const rows = this.tbodyTarget.querySelectorAll('tr')

    rows.forEach(row => {
      const firstTd = row.querySelector('td:first-child')
      if (!firstTd) return

      // Extract record ID from the existing checkbox/link
      const existingCheckbox = firstTd.querySelector('input[type="checkbox"]')
      if (!existingCheckbox) return

      const recordId = existingCheckbox.id.split('_').pop()

      // Create new Stimulus-compatible checkbox
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.dataset.id = recordId
      checkbox.dataset.action = 'click->data-table#toggleSelection'
      checkbox.checked = this.selected.has(recordId)
      checkbox.className = 'selected-checkbox'

      // Replace the entire td content with just the checkbox
      firstTd.innerHTML = ''
      firstTd.appendChild(checkbox)
    })

    // Update selection display after rows are loaded
    this.updateSelectionDisplay()
  }

  // Toggle selection for a single row
  toggleSelection(event) {
    const checkbox = event.currentTarget
    const id = checkbox.dataset.id

    // Let the checkbox do its default behavior, then sync our Set
    if (checkbox.checked) {
      this.selected.add(id)
    } else {
      this.selected.delete(id)
    }

    this.updateSelectionDisplay()
    logger.log('Selection toggled:', id, 'Total selected:', this.selected.size)
  }

  // Toggle select all/none (triggered by header checkbox)
  toggleSelectAll(event) {
    const headerCheckbox = event.currentTarget

    if (headerCheckbox.checked) {
      this.selectAll()
    } else {
      this.selectNone()
    }
  }

  // Select all IDs (including hidden pages)
  selectAll() {
    // Select all IDs matching the current filter (including hidden pages)
    this.allIds.forEach(id => {
      this.selected.add(id.toString())
    })

    // Check visible checkboxes
    const checkboxes = this.element.querySelectorAll('tbody input[type="checkbox"][data-id]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = true
    })

    this.updateSelectionDisplay()
    logger.log('Selected all IDs (including hidden pages). Total selected:', this.selected.size)
  }

  // Clear all selection
  selectNone() {
    this.selected.clear()

    const checkboxes = this.element.querySelectorAll('tbody input[type="checkbox"][data-id]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    this.updateSelectionDisplay()
    logger.log('Selection cleared')
  }

  // Check if an ID is selected
  isSelected(id) {
    return this.selected.has(id)
  }

  // Update selection display (count, mass actions menu visibility, etc.)
  updateSelectionDisplay() {
    const count = this.selected.size

    // Check for selected items on other pages (for inline warning text)
    const visibleCheckboxes = this.element.querySelectorAll('tbody input[type="checkbox"][data-id]')
    const visibleIds = new Set(Array.from(visibleCheckboxes).map(cb => cb.dataset.id))
    const hiddenSelectedCount = Array.from(this.selected).filter(id => !visibleIds.has(id)).length

    // Update selection count text (with inline hidden selection note if applicable)
    if (this.hasSelectionCountTarget) {
      let countText = (this.i18n.selection_count || '{count} נבחרו')
        .replace('{count}', count)

      // Add inline note about hidden selections
      if (hiddenSelectedCount > 0) {
        const hiddenText = this.i18n.including_hidden || '(כולל תוצאות מוסתרות)'
        countText += ' ' + hiddenText
      }

      this.selectionCountTarget.textContent = countText
    }

    // Show/hide selection info (count + hidden warning inline)
    if (this.hasSelectionInfoTarget) {
      if (count > 0) {
        this.selectionInfoTarget.classList.remove('d-none')
      } else {
        this.selectionInfoTarget.classList.add('d-none')
      }
    }

    // Always show mass actions menu (for export all functionality)
    // Individual menu items can be disabled based on selection
    if (this.hasMassActionsTarget) {
        this.massActionsTarget.classList.remove('d-none')
    }

    // Update "Export Selected" link disabled state
    const exportSelectedLink = document.getElementById('export-selected')
    if (exportSelectedLink) {
      if (count === 0) {
        exportSelectedLink.classList.add('disabled')
        exportSelectedLink.setAttribute('aria-disabled', 'true')
      } else {
        exportSelectedLink.classList.remove('disabled')
        exportSelectedLink.removeAttribute('aria-disabled')
      }
    }

    // Update header checkbox state
    const allChecked = visibleCheckboxes.length > 0 &&
                       Array.from(visibleCheckboxes).every(cb => cb.checked)
    const headerCheckbox = this.element.querySelector('thead input[type="checkbox"]')
    if (headerCheckbox) {
      headerCheckbox.checked = allChecked
    }

    // Emit custom event for other controllers/UI elements
    this.element.dispatchEvent(new CustomEvent('data-table:selection-changed', {
      detail: {
        count,
        selectedIds: Array.from(this.selected)
      },
      bubbles: true
    }))
  }

  // ========================================
  // Column Visibility Methods (Phase 4 - Optional)
  // ========================================

  // Initialize column visibility toggle (only if columnToggle target exists)
  initializeColumnVisibility() {
    // Check for modal checkboxes first, fall back to dropdown
    const checkboxes = this.hasColumnSelectorCheckboxTarget
      ? this.columnSelectorCheckboxTargets
      : (this.hasColumnToggleTarget
          ? this.columnToggleTarget.querySelectorAll('input[type="checkbox"][data-column-index]')
          : [])

    if (checkboxes.length === 0) {
      logger.log('Column visibility toggle not enabled (no column selector checkboxes)')
      return
    }

    checkboxes.forEach(checkbox => {
      const columnIndex = parseInt(checkbox.dataset.columnIndex)
      if (!isNaN(columnIndex) && checkbox.checked) {
        this.visibleColumns.add(columnIndex)
      }
    })

    logger.log('Column visibility initialized:', Array.from(this.visibleColumns))
  }

  // Toggle visibility of a column
  async toggleColumn(event) {
    const checkbox = event.currentTarget
    const columnIndex = parseInt(checkbox.dataset.columnIndex)

    if (isNaN(columnIndex)) {
      logger.warn('Invalid column index for toggle')
      return
    }

    if (checkbox.checked) {
      this.visibleColumns.add(columnIndex)
    } else {
      this.visibleColumns.delete(columnIndex)
    }

    logger.log('Column toggled:', columnIndex, 'Visible:', checkbox.checked)

    // Clear ALL filters when column visibility changes
    // This is necessary because column indices shift when columns are hidden/shown
    // (visible columns are numbered sequentially: 1, 2, 3... not by all_columns index)
    if (Object.keys(this.filters).length > 0) {
      this.filters = {}
      logger.log('Cleared all filters due to column visibility change')
      this.updateResetFiltersButtonVisibility()
    }

    // Save to server (persist to user settings)
    await this.saveColumnVisibility()

    // Reload data from server with headers (to rebuild thead with new columns)
    // Only column visibility changes need header reload to prevent filter input focus loss
    this.loadData({ reloadHeaders: true })
  }

  // Save column visibility preferences to server
  async saveColumnVisibility() {
    // Get column names and visibility states from modal checkboxes
    // Use targets instead of querySelector since modal is rendered outside the table
    const checkboxes = this.hasColumnSelectorCheckboxTarget
      ? this.columnSelectorCheckboxTargets
      : this.columnToggleTarget.querySelectorAll('input[type="checkbox"][data-column-index]')
    const columnData = {}

    checkboxes.forEach(checkbox => {
      const columnName = checkbox.dataset.columnName
      if (columnName) {
        columnData[columnName] = checkbox.checked
      }
    })

    // Get the save URL from data attribute (if provided by view)
    const saveUrl = this.element.dataset.columnSaveUrl
    if (!saveUrl) {
      logger.warn('No column save URL provided - column visibility will not persist')
      return
    }

    try {
      const response = await fetch(saveUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ column_selector: columnData })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      logger.log('Column visibility saved to server')
    } catch (error) {
      logger.error('Error saving column visibility:', error)
    }
  }

  // ========================================
  // Export Methods
  // ========================================

  // Export current table data to Excel
  async exportTable(event) {
    event.preventDefault()
    const exportAll = event.currentTarget.dataset.exportAll === 'true'

    // Collect current table state
    const selectedIds = exportAll ? [] : Array.from(this.selected)
    const filters = JSON.stringify(this.filters)
    const sorts = JSON.stringify(this.sorts)
    const visibleColumns = Array.from(this.visibleColumns)

    // Get the export URL - remove .json and any query params, then add export action
    // Use exportActionValue if provided (for has_users/has_employees), otherwise default to export_index
    const baseUrl = this.urlValue.split('?')[0].replace('.json', '').replace(/\/$/, '')
    const exportAction = this.hasExportActionValue ? this.exportActionValue : 'export_index'
    const exportUrl = baseUrl + '/' + exportAction

    logger.log('Exporting table:', {
      exportAll,
      selectedCount: selectedIds.length,
      filters: this.filters,
      sorts: this.sorts,
      visibleColumns: visibleColumns
    })

    try {
      // Post to export endpoint
      const response = await fetch(exportUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          selected_ids: selectedIds,
          filters: filters,
          sorts: sorts,
          visible_columns: visibleColumns
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.error) {
        throw new Error(data.error)
      }

      // Show report status modal (reuse existing report controller)
      this.showReportStatus(data.report_cache_id)
    } catch (error) {
      logger.error('Export failed:', error)
      alert(`Export failed: ${error.message}`)
    }
  }

  // ========================================
  // Aggregation Methods
  // ========================================

  // Toggle aggregate row visibility
  async toggleAggregates(event) {
    event.preventDefault()

    if (!this.hasAggregateToggleUrlValue) {
      logger.warn('No aggregate toggle URL configured')
      return
    }

    const button = event.currentTarget
    button.disabled = true

    try {
      const response = await fetch(this.aggregateToggleUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      this.showAggregatesValue = data.show_aggregates

      // Update checkbox state
      if (this.hasAggregateCheckboxTarget) {
        this.aggregateCheckboxTarget.checked = data.show_aggregates
      }

      logger.log('Aggregates toggled:', data.show_aggregates)

      // Reload data with/without aggregates
      this.loadData()
    } catch (error) {
      logger.error('Error toggling aggregates:', error)
    }
  }

  // Show/hide aggregate toggle menu item based on whether table has aggregatable columns
  updateAggregateToggleVisibility(hasAggregatable) {
    if (!this.hasAggregateToggleTarget) return

    if (hasAggregatable) {
      this.aggregateToggleTarget.classList.remove('d-none')
      // Also show the divider
      if (this.hasAggregateDividerTarget) {
        this.aggregateDividerTarget.classList.remove('d-none')
      }
      // Update checkbox state based on current value
      if (this.hasAggregateCheckboxTarget) {
        this.aggregateCheckboxTarget.checked = this.showAggregatesValue
      }
    } else {
      this.aggregateToggleTarget.classList.add('d-none')
      if (this.hasAggregateDividerTarget) {
        this.aggregateDividerTarget.classList.add('d-none')
      }
    }
  }

  // Render aggregate values in the thead row (after filter row)
  renderAggregateRow() {
    if (!this.hasAggregateRowTarget) return

    // Get visible headers (including their column names/refs)
    const visibleHeaders = this.element.querySelectorAll('thead th:not(.d-none)')
    let html = ''

    visibleHeaders.forEach((th, index) => {
      // Look up aggregate by column_ref first (for wrapped columns with parameters),
      // then fall back to column_name (for base columns)
      const columnRef = th.dataset.columnRef
      const columnName = th.dataset.columnName
      const aggregateValue = (columnRef && this.aggregates[columnRef] !== undefined)
        ? this.aggregates[columnRef]
        : (columnName ? this.aggregates[columnName] : undefined)

      if (aggregateValue !== undefined && aggregateValue !== null) {
        // Format number with Hebrew locale
        const formatted = new Intl.NumberFormat('he-IL', {
          maximumFractionDigits: 2
        }).format(aggregateValue)
        html += `<td class="aggregate-cell fw-bold text-end">${formatted}</td>`
      } else {
        // Empty cell for non-aggregatable columns (like checkbox, actions)
        html += '<td></td>'
      }
    })

    this.aggregateRowTarget.innerHTML = html
    this.aggregateRowTarget.classList.remove('d-none')

    logger.log('Rendered aggregate row with values:', this.aggregates)
  }

  // Show report generation status using the same system as regular reports
  showReportStatus(reportCacheId) {
    logger.log('Showing report status for cache ID:', reportCacheId)

    // Store report cache ID in sessionStorage for tab-specific restoration
    logger.log('📊 data_table: Storing report cache ID in sessionStorage:', reportCacheId)
    let activeReports = JSON.parse(sessionStorage.getItem('activeReportCaches') || '[]')
    logger.log('📊 data_table: Current activeReports before:', activeReports)

    if (!activeReports.includes(reportCacheId)) {
      activeReports.push(reportCacheId)
      sessionStorage.setItem('activeReportCaches', JSON.stringify(activeReports))
      logger.log('📊 data_table: Updated activeReports after:', activeReports)
      logger.log('📊 data_table: Verification - stored value:', sessionStorage.getItem('activeReportCaches'))
    } else {
      logger.log('📊 data_table: Report ID already in sessionStorage, skipping')
    }

    // Find or create the report statuses container (same as reports/show.js.erb)
    let reportStatusesContainer = document.getElementById('reportStatuses')
    if (!reportStatusesContainer) {
      reportStatusesContainer = document.createElement('div')
      reportStatusesContainer.id = 'reportStatuses'
      reportStatusesContainer.className = 'sidebar-aware'

      // Apply sidebar collapsed class if sidebar is collapsed
      const sidebarController = document.body.querySelector('[data-controller*="sidebar"]')
      if (sidebarController) {
        const sidebarCollapsed = sidebarController.dataset.sidebarCollapsedValue === 'true'
        if (sidebarCollapsed) {
          reportStatusesContainer.classList.add('sidebar-collapsed')
        }
      } else {
        // Fallback: check main-content-wrapper for sidebar state
        const mainContent = document.querySelector('.main-content-wrapper')
        if (mainContent && mainContent.classList.contains('sidebar-collapsed')) {
          reportStatusesContainer.classList.add('sidebar-collapsed')
        }
      }

      document.body.appendChild(reportStatusesContainer)

      // Force a reflow to ensure CSS is applied before adding content
      // This ensures the width/margin calculations are correct when sidebar is collapsed
      reportStatusesContainer.offsetHeight
    }

    // Add the report status (using same structure as _report_status.slim)
    const reportStatusHTML = `
      <div class="report-status" id="reportStatus${reportCacheId}" data-controller="report" data-report-report-cache-id-value="${reportCacheId}">
        <div class="report-status-loader" data-report-target="status"></div>
        <span class="report-status-message" data-report-target="message">מכין ייצוא...</span>
        <button class="report-status-close" data-report-target="close" data-action="click->report#close" aria-label="Close">×</button>
      </div>
    `
    reportStatusesContainer.insertAdjacentHTML('beforeend', reportStatusHTML)

    // Function to update CSS custom property for overlay height
    function updateReportStatusOverlayHeight() {
      const container = document.getElementById('reportStatuses')
      if (container && container.classList.contains('show')) {
        requestAnimationFrame(() => {
          const height = container.offsetHeight
          document.documentElement.style.setProperty('--report-status-overlay-height', height + 'px')
        })
      } else {
        document.documentElement.style.setProperty('--report-status-overlay-height', '0px')
      }
    }

    // Expose function globally for report controller to use
    window.updateReportStatusOverlayHeight = updateReportStatusOverlayHeight

    // Show the container after a brief delay
    setTimeout(() => {
      reportStatusesContainer.classList.add('show')
      // Update overlay height CSS variable after showing
      setTimeout(() => {
        updateReportStatusOverlayHeight()
      }, 50)
    }, 1)
  }
}
